import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Section, SectionItem, SectionHeader } from '@/components';
import { getCaseStudyBySlug, getRelatedCaseStudies, type CaseStudy } from '@/data/resources';

export function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>();
  const caseStudy = slug ? getCaseStudyBySlug(slug) : undefined;

  if (!caseStudy) {
    return <Navigate to="/resources" replace />;
  }

  const relatedStudies = getRelatedCaseStudies(caseStudy.slug, 2);

  return (
    <>
      <title>{caseStudy.title} | Omniforge Case Study</title>
      <meta
        name="description"
        content={caseStudy.excerpt}
      />

      {/* Hero Section */}
      <section className="bg-ink pt-32 pb-16">
        <div className="container-marketing">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-gray-400">
                <li>
                  <Link to="/resources" className="hover:text-white transition-colors">
                    Resources
                  </Link>
                </li>
                <li>
                  <ChevronRightIcon className="w-4 h-4" />
                </li>
                <li>
                  <span className="text-gray-300">Case Studies</span>
                </li>
              </ol>
            </nav>

            {/* Company Info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center">
                <BuildingIcon className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h2 className="text-xl text-paper font-semibold">{caseStudy.company}</h2>
                <span className="inline-block px-3 py-1 rounded-full bg-accent/20 text-accent text-sm font-medium">
                  {caseStudy.industry}
                </span>
              </div>
            </div>

            {/* Title */}
            <h1 className="font-display text-h1 text-paper mb-8 max-w-4xl">
              {caseStudy.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Key Results Banner */}
      <section className="bg-accent py-8">
        <div className="container-marketing">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {caseStudy.results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {result.value}
                </div>
                <div className="text-sm text-white/80">{result.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Challenge Section */}
      <Section background="paper">
        <div className="max-w-3xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 mb-6">The Challenge</h2>
            <div className="prose prose-lg text-muted">
              {caseStudy.challenge.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 whitespace-pre-line">
                  {formatMarkdownBold(paragraph)}
                </p>
              ))}
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* Solution Section */}
      <Section background="cream">
        <div className="max-w-3xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 mb-6">The Solution</h2>
            <div className="prose prose-lg text-muted">
              {caseStudy.solution.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 whitespace-pre-line">
                  {formatMarkdownBold(paragraph)}
                </p>
              ))}
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* Results Section */}
      <Section background="paper">
        <div className="max-w-3xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 mb-6">The Results</h2>
            <div className="prose prose-lg text-muted">
              {caseStudy.detailedResults.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 whitespace-pre-line">
                  {formatMarkdownBold(paragraph)}
                </p>
              ))}
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* Quote/Testimonial Section */}
      <Section background="ink">
        <div className="max-w-3xl mx-auto">
          <SectionItem>
            <QuoteCallout
              quote={caseStudy.quote}
              person={caseStudy.quotePerson}
              role={caseStudy.quoteRole}
              company={caseStudy.company}
            />
          </SectionItem>
        </div>
      </Section>

      {/* About Company Section */}
      <Section background="cream">
        <div className="max-w-3xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 mb-6">About {caseStudy.company}</h2>
            <div className="prose prose-lg text-muted mb-8">
              {caseStudy.companyDescription.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
            {caseStudy.companyStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {caseStudy.companyStats.employees && (
                  <CompanyStat label="Employees" value={caseStudy.companyStats.employees} />
                )}
                {caseStudy.companyStats.location && (
                  <CompanyStat label="Location" value={caseStudy.companyStats.location} />
                )}
                {caseStudy.companyStats.founded && (
                  <CompanyStat label="Founded" value={caseStudy.companyStats.founded} />
                )}
                {caseStudy.companyStats.revenue && (
                  <CompanyStat label="Revenue" value={caseStudy.companyStats.revenue} />
                )}
              </div>
            )}
          </SectionItem>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="accent">
        <div className="max-w-2xl mx-auto text-center">
          <SectionItem>
            <h2 className="font-display text-h2 text-white mb-4">
              Get Similar Results for Your Business
            </h2>
            <p className="text-white/80 text-lg mb-8">
              See how Omniforge can transform your operations. Schedule a personalized demo
              and discover why companies like {caseStudy.company} chose us.
            </p>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 bg-white text-accent px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule Your Demo
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </SectionItem>
        </div>
      </Section>

      {/* Related Case Studies */}
      {relatedStudies.length > 0 && (
        <Section background="paper">
          <SectionHeader
            title="More Success Stories"
            subtitle="See how other companies have transformed their operations with Omniforge"
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {relatedStudies.map((study) => (
              <SectionItem key={study.id}>
                <RelatedCaseStudyCard study={study} />
              </SectionItem>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

interface QuoteCalloutProps {
  quote: string;
  person: string;
  role: string;
  company: string;
}

function QuoteCallout({ quote, person, role, company }: QuoteCalloutProps) {
  return (
    <div className="relative">
      <QuoteIcon className="absolute -top-4 -left-4 w-16 h-16 text-accent/30" />
      <blockquote className="relative z-10">
        <p className="text-xl md:text-2xl text-paper font-medium leading-relaxed mb-6">
          "{quote}"
        </p>
        <footer className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-accent" />
          </div>
          <div>
            <cite className="not-italic font-semibold text-paper">{person}</cite>
            <p className="text-gray-400 text-sm">{role}, {company}</p>
          </div>
        </footer>
      </blockquote>
    </div>
  );
}

interface CompanyStatProps {
  label: string;
  value: string;
}

function CompanyStat({ label, value }: CompanyStatProps) {
  return (
    <div className="bg-white rounded-lg p-4 text-center shadow-soft">
      <div className="text-lg font-bold text-ink">{value}</div>
      <div className="text-sm text-muted">{label}</div>
    </div>
  );
}

interface RelatedCaseStudyCardProps {
  study: CaseStudy;
}

function RelatedCaseStudyCard({ study }: RelatedCaseStudyCardProps) {
  return (
    <Link
      to={study.href}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[3/2] overflow-hidden">
        <img
          src={study.imageUrl}
          alt={study.company}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium">
            {study.industry}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-accent transition-colors">
          {study.title}
        </h3>
        <p className="text-muted text-sm mb-4 line-clamp-2">{study.excerpt}</p>
        <div className="flex gap-4">
          {study.results.slice(0, 2).map((result, i) => (
            <div key={i} className="text-center">
              <div className="text-xl font-bold text-accent">{result.value}</div>
              <div className="text-xs text-muted">{result.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
}

// ============================================
// HELPER FUNCTION
// ============================================

function formatMarkdownBold(text: string): React.ReactNode {
  // Split by **text** pattern and render bold segments
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, index) => {
    // Odd indices are the content between **
    if (index % 2 === 1) {
      return <strong key={index} className="font-semibold text-ink">{part}</strong>;
    }
    return part;
  });
}

// ============================================
// ICONS
// ============================================

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );
}

function QuoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
